<template>
  <div>
    <div class="grid grid-cols-2 gap-2" v-if="!selectedOrder">
      <h1 class="my-2">
        <label for="status">Status</label>
        <select
          name="status"
          id="status"
          class="default-select bordered dull"
          v-model="filters.status"
        >
          <option v-for="option in options" :key="option.value">
            {{ option.value }}
          </option>
        </select>
      </h1>
      <div class="form-group input-box flex flex-col">
        <label for="amount">Order Number</label>
        <input
          type="text"
          id="order-number"
          class="bordered"
          v-model="filters.orderNumber"
        />
      </div>
    </div>
    <div v-if="!selectedOrder">
      <table class="default-table">
        <thead>
          <th>Order Number</th>
          <th>Customer</th>
          <th>Amount</th>
          <th>Not Paid</th>
          <th>Actions</th>
        </thead>
        <tbody class="overflow-y-scroll">
          <tr v-for="order in orders" :key="order.orderId">
            <td>{{ order.orderNumber }}</td>
            <td>{{ order.shippingAddressName }}</td>
            <td>{{ order.total | currencyFormatted(true) }}</td>
            <td>{{ order.notPaid | currencyFormatted(true) }}</td>
            <td>
              <button class="btn-dark-button" @click="selectOrder(order)">
                Pay
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="footer w-full flex justify-center">
        <div class="pages flex flex-row">
          <div>
            <a
              :class="{ disabled: currentPage == 1 }"
              class="page-link"
              @click="previous()"
              >Previous</a
            >
          </div>
          <div>Page {{ currentPage }} of {{ totalPages }} pages</div>
          <div>
            <a
              :class="{
                disabled: totalPages > currentPage
              }"
              class="page-link"
              @click="next()"
              >Next</a
            >
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <div class="title flex flex-row justify-between">
        <h2>
          Order Number: {{ selectedOrder.orderNumber }}
          <router-link
            :to="{
              name: 'OrdersPage',
              query: { orderOpen: selectedOrder.orderNumber }
            }"
            target="_blank"
            class="text-blue-500"
          >
            Open Order
          </router-link>
        </h2>
        <button class="btn-dark-button" @click="unselectOrder">
          Back
        </button>
      </div>

      <div class="form-group input-box flex flex-col">
        <label for="amount"
          >Amount
          <span class="text-red-500"
            >Can be more or less than the current amount</span
          ></label
        >
        <input
          type="number"
          step="0.01"
          id="amount"
          class="bordered"
          :disabled="confirmed"
          v-model.number="amount"
        />
      </div>
      <div class="flex items-center gap-2 mb-2">
        <input
          type="checkbox"
          id="applyFee"
          v-model="applyFee"
          :disabled="confirmed"
        />
        <label for="applyFee" class="mb-0">Apply payment fee</label>
      </div>
      <div class="bg-gray-800 text-white p-2 mb-2">
        Email:
        <b>
          {{ selectedOrder.customerEmail }}
        </b>
      </div>
      <div>
        <h2>Billing address</h2>
        <div class="input-box-divided">
          <div class="form-group input-box flex flex-col">
            <label for="billingAddressName">Name</label>
            <input
              type="text"
              id="billingAddressName"
              class="bordered"
              :disabled="confirmed"
              v-model="selectedOrder.billingAddressName"
            />
          </div>
          <div class="form-group input-box flex flex-col">
            <label for="billingAddressStreet1">Street</label>
            <input
              type="text"
              id="billingAddressStreet1"
              class="bordered"
              :disabled="confirmed"
              v-model="selectedOrder.billingAddressStreet1"
            />
          </div>
        </div>
        <div class="input-box-divided">
          <div class="form-group input-box flex flex-col">
            <label for="billingAddressCity">City</label>
            <input
              type="text"
              id="billingAddressCity"
              class="bordered"
              :disabled="confirmed"
              v-model="selectedOrder.billingAddressCity"
            />
          </div>
          <div class="form-group input-box flex flex-col">
            <label for="billingAddressState">State</label>
            <input
              type="text"
              id="billingAddressState"
              class="bordered"
              :disabled="confirmed"
              v-model="selectedOrder.billingAddressState"
            />
          </div>
        </div>
        <div class="input-box-divided">
          <div class="form-group input-box flex flex-col">
            <label for="billingAddressCountry">Country</label>
            <input
              type="text"
              id="billingAddressCountry"
              class="bordered"
              :disabled="confirmed"
              v-model="selectedOrder.billingAddressCountry"
            />
          </div>
          <div class="form-group input-box flex flex-col">
            <label for="billingAddressPostalCode">Postal code</label>
            <input
              type="text"
              id="billingAddressPostalCode"
              class="bordered"
              :disabled="confirmed"
              v-model="selectedOrder.billingAddressPostalCode"
            />
          </div>
        </div>
        <h3 v-if="selectedOrder.total !== selectedOrder.notPaid">
          Order Total
          <b>{{ selectedOrder.total | currencyFormatted(true) }}</b> - Not Paid
          <b>{{ selectedOrder.notPaid | currencyFormatted(true) }}</b>
        </h3>
        <h3 v-else>
          Order Total
          <b>{{ selectedOrder.notPaid | currencyFormatted(true) }}</b>
        </h3>
        <button
          class="btn-dark-button"
          @click="confirmDataForPayment"
          v-if="!confirmed && amount > 0"
        >
          Confirm
        </button>
      </div>
      <hr class="my-2" />
      <div v-if="confirmed">
        <h3>
          Payment
          <span class="text-red-500">(${{ totalWithFee || amount }})</span>
        </h3>

        <div v-if="feeAmount > 0" class="bg-gray-100 rounded p-3 mb-3 text-sm">
          <div class="flex justify-between">
            <span>Subtotal</span>
            <span>${{ Number(amount).toFixed(2) }}</span>
          </div>
          <div class="flex justify-between text-orange-600">
            <span>Payment fee</span>
            <span>+${{ feeAmount.toFixed(2) }}</span>
          </div>
          <div class="flex justify-between font-bold border-t mt-1 pt-1">
            <span>Total charged</span>
            <span>${{ totalWithFee.toFixed(2) }}</span>
          </div>
        </div>

        <div v-if="loadingStripe" class="flex items-center gap-2 py-4">
          <BaseMiniSpinner />
          <span>Initializing payment...</span>
        </div>

        <div v-else-if="paymentSuccess" class="text-center py-8">
          <div class="text-5xl mb-3">✓</div>
          <h3 class="text-xl font-bold text-green-600">Payment Successful!</h3>
          <button
            v-if="posTransactionId"
            class="mt-3 text-blue-600 underline text-sm"
            type="button"
            @click="$emit('openReceipt', posTransactionId)"
          >
            View POS Receipt #{{ posTransactionId }}
          </button>
        </div>

        <StripeCardSection
          v-else-if="stripe && clientSecret"
          :stripe="stripe"
          :clientSecret="clientSecret"
          :amount="totalWithFee || amount"
          :customerEmail="selectedOrder.customerEmail"
          :billingAddress="{
            name: selectedOrder.billingAddressName,
            postal_code: selectedOrder.billingAddressPostalCode,
            country: selectedOrder.billingAddressCountry
          }"
          @confirmed="onPaymentConfirmed"
        >
          <template v-slot:cancel>
            <button
              type="button"
              class="px-6 py-3 border border-gray-300 rounded-md font-semibold hover:bg-gray-100"
              @click="unselectOrder"
            >
              Cancel
            </button>
          </template>
        </StripeCardSection>

        <div
          v-if="stripeError"
          class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mt-4"
        >
          {{ stripeError }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from "lodash";
import { loadStripe } from "@stripe/stripe-js";
import axios from "axios";
import { getOrders } from "@/api/store/OrdersService";
import StripeCardSection from "@/components/payments/StripeCardSection.vue";

export default {
  name: "QuickOrderPayment",
  components: { StripeCardSection },
  mounted() {
    this.loadOrders();
  },
  data() {
    return {
      orders: [],
      loadingOrders: false,
      selectedOrder: null,
      amount: 0,
      applyFee: false,
      feeAmount: 0,
      totalWithFee: 0,
      confirmed: false,
      stripe: null,
      clientSecret: "",
      paymentIntentId: "",
      loadingStripe: false,
      stripeError: null,
      paymentSuccess: false,
      posTransactionId: null,
      pages: 0,
      ordersByPage: 20,
      currentPage: 1,
      totalPages: 0,
      filters: {
        status: "awaiting_payment",
        orderNumber: ""
      },
      options: [
        { displayText: "Awaiting Shipment", value: "awaiting_shipment" },
        { displayText: "Awaiting Payment", value: "awaiting_payment" },
        { displayText: "Shipped", value: "shipped" },
        { displayText: "On Hold", value: "on_hold" },
        { displayText: "VIN verification", value: "VIN_verification" }
      ]
    };
  },
  watch: {
    filters: {
      deep: true,
      handler: debounce(function() {
        this.currentPage = 1;
        this.loadOrders();
      }, 300)
    }
  },
  methods: {
    previous() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.loadOrders();
      }
    },
    next() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.loadOrders();
      }
    },
    unselectOrder() {
      this.selectedOrder = null;
      this.amount = 0;
      this.applyFee = false;
      this.feeAmount = 0;
      this.totalWithFee = 0;
      this.confirmed = false;
      this.stripe = null;
      this.clientSecret = "";
      this.paymentIntentId = "";
      this.stripeError = null;
      this.paymentSuccess = false;
      this.posTransactionId = null;
      this.loadOrders();
    },
    selectOrder(order) {
      this.selectedOrder = order;
      this.amount = parseFloat(parseFloat(order.notPaid).toFixed(2));
      this.applyFee = false;
      this.feeAmount = 0;
      this.totalWithFee = 0;
      this.stripe = null;
      this.clientSecret = "";
      this.paymentIntentId = "";
      this.stripeError = null;
      this.paymentSuccess = false;
      this.posTransactionId = null;
      this.confirmed = false;
    },
    async confirmDataForPayment() {
      this.confirmed = true;
      this.loadingStripe = true;
      this.stripeError = null;
      try {
        if (!this.stripe) {
          this.stripe = await loadStripe(
            process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY
          );
        }
        const response = await axios.post(
          process.env.VUE_APP_WEB_API +
            "/stripex/payment_intent_existing_order",
          {
            order_number: this.selectedOrder.orderNumber,
            amount: parseFloat(this.amount),
            saveCard: false,
            isAdmin: true,
            applyFee: this.applyFee
          },
          {
            headers: {
              Authorization: this.$store.getters["auth/currentToken"]
            }
          }
        );
        this.clientSecret = response.data.secret;
        this.paymentIntentId = response.data.paymentIntentId;
        this.feeAmount = response.data.feeAmount || 0;
        this.totalWithFee =
          response.data.totalWithFee || parseFloat(this.amount);
      } catch (e) {
        this.stripeError =
          e.response?.data?.error ||
          e.response?.data?.message ||
          e.message ||
          "Failed to initialize payment";
        this.confirmed = false;
      } finally {
        this.loadingStripe = false;
      }
    },
    async onPaymentConfirmed(paymentIntentId) {
      try {
        const maxAttempts = 30;
        for (let i = 0; i < maxAttempts; i++) {
          const res = await axios.get(
            process.env.VUE_APP_WEB_API +
              `/stripex/verify_payment/${paymentIntentId}`
          );
          if (res.data.status === "succeeded") {
            this.paymentSuccess = true;
            this.posTransactionId = res.data.pos_transaction_id || null;
            this.$toast.open({
              message: "Payment processed successfully",
              type: "success",
              position: "top-right"
            });
            this.$emit(
              "newPaymentProcessed",
              this.posTransactionId || paymentIntentId
            );
            return;
          }
          if (res.data.status === "failed") {
            this.stripeError =
              res.data.message || "Payment verification failed.";
            return;
          }
          await new Promise(r => setTimeout(r, 1000));
        }
        this.stripeError = "Payment verification timed out.";
      } catch (e) {
        this.stripeError =
          e.response?.data?.message || "Failed to verify payment";
      }
    },
    loadOrders() {
      this.loadingOrders = true;
      getOrders({
        statusShipment: this.filters.status,
        limitPerPage: 20,
        page: this.currentPage,
        orderNumber: this.filters.orderNumber
      })
        .then(res => {
          this.orders = res.orders;
          this.pages = res.totalItems / this.ordersByPage;
          this.totalPages = res.totalPages;
        })
        .catch(e => {
          console.error(e);
        })
        .finally(() => {
          this.loadingOrders = false;
        });
    }
  }
};
</script>

<style scoped lang="postcss">
h3 {
  font-size: large;
}
</style>

<style lang="scss" scoped>
@import "@/assets/style/css/generic-variables.scss";

.content-main {
  .content {
    padding: 5px;
    width: 100%;
    .header {
      width: 100%;
      padding: 20px 15px;
    }
    .table {
      width: 100%;
      height: calc(100% - 200px);
      overflow-x: auto;
      margin-bottom: 10px;
      table {
        td {
          font-size: 14px;
          white-space: nowrap;
        }
      }
    }
    .footer {
      display: flex;
      flex-direction: row;
      width: 100%;
      justify-content: space-between;
      .pages {
        display: flex;
        gap: 15px;
        flex-direction: row;
        .page-link {
          color: $link-color-1;
        }
      }
    }
  }
}
</style>
